import moment from 'moment';
import React from 'react';
import { Link } from 'react-router-dom';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown, UncontrolledTooltip } from 'reactstrap';

const RegistrationFlowListItem = ({ flow, organization, event, toggleDeleteFlowModal }) => {
    const isPublished = false;
    const status = isPublished ? 'active' : 'inactive';

    const startDateTimeString = `${flow.startDate} ${flow.startTime}`;
    const endDateTimeString = `${flow.endDate} ${flow.endTime}`;

    const startOffsetDays = flow.startOffsetDays || 0;
    const endOffsetDays = flow.endOffsetDays || 0;

    const handleExternalLinkClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        const hostname = organization.hostname || `${organization.identifier}.simple.events`;
        const url = `https://${hostname}/${event.nameSlug}/${flow.page.pathSlug}`;
        window.open(url, '_blank');
    };

    const handleDropdownClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    const formatDateRange = () => {
        const endDescription = endOffsetDays == 0 ? 'on event day' : `${endOffsetDays} days after event starts`;
        return `Registration opens ${startOffsetDays} days before event, closes ${endDescription}.`;
    };

    // Calculate registration stats
    const totalRegistrations = flow.registrationGroup?.attendees?.length || 0;
    const maxCapacity = flow.registrationGroup?.maxCapacity || 0;
    const capacityPercentage = maxCapacity ? Math.min(100, Math.round((totalRegistrations / maxCapacity) * 100)) : 0;

    // Calculate ticket stats
    const totalTickets = flow.tickets.reduce((sum, ticket) => sum + ticket.quantity, 0);
    const totalSold = flow.tickets.reduce((sum, ticket) => sum + ticket.sold, 0);
    const ticketSalesPercentage = totalTickets ? Math.min(100, Math.round((totalSold / totalTickets) * 100)) : 0;

    // Calculate estimated revenue
    const totalRevenue = flow.tickets.reduce((sum, ticket) => sum + (ticket.sold * ticket.price), 0);
    const maxRevenue = flow.tickets.reduce((sum, ticket) => sum + (ticket.quantity * ticket.price), 0);

    // Determine if registration is currently active
    const isActive = moment().isBetween(moment(startDateTimeString), moment(endDateTimeString));
    const isUpcoming = moment().isBefore(moment(startDateTimeString));
    const isPast = moment().isAfter(moment(endDateTimeString));

    return (
        <Link
            to={`./registrationFlows/${flow._id}`}
            className={`registration-flow-item ${isActive ? 'active' : ''}`}
        >
            {/* Status indicator stripe */}
            {/* <div className={`status-stripe ${isPublished
                ? (isActive ? 'active' : isUpcoming ? 'upcoming' : 'past')
                : 'unpublished'}`}
            /> */}

            {/* Header Section */}
            <div className="header-section">
                <div className="header-content">
                    <div className="title-container">
                        <h2 className="flow-title">{flow.name}</h2>
                        <div className={`status-badge ${status}`}>
                            {status}
                        </div>
                    </div>
                    <div className="date-status-container">
                        <span className="date-range">
                            {formatDateRange()}
                        </span>
                        {status === 'active' && <>
                            {isActive && (
                                <span className="status-indicator active">
                                    <i className="las la-clock"></i>
                                    Currently open
                                </span>
                            )}
                            {isUpcoming && (
                                <span className="status-indicator upcoming">
                                    <i className="las la-hourglass-start"></i>
                                    Starts in {moment(startDateTimeString).fromNow(true)}
                                </span>
                            )}
                            {isPast && (
                                <span className="status-indicator past">
                                    <i className="las la-hourglass-end"></i>
                                    Ended {moment(endDateTimeString).fromNow()}
                                </span>
                            )}
                        </>}
                    </div>
                    {flow.pageTemplate && flow.pageTemplate.pathSlug && (
                        <span className="page-path">
                            <i className="las la-link"></i>
                            <span className="path-text">/EventName/{flow.pageTemplate.pathSlug}</span>
                            <span className={`visibility-indicator ${flow.pageTemplate.openToPublic ? 'public' : flow.pageTemplate.internalOnly ? 'internal' : 'private'}`}>
                                <i className={`las ${flow.pageTemplate.openToPublic ? 'la-globe' : flow.pageTemplate.internalOnly ? 'la-user-shield' : 'la-lock'}`}></i>
                                <span className="visibility-text">{flow.pageTemplate.openToPublic ? 'Public' : flow.pageTemplate.internalOnly ? 'Internal' : 'Private'}</span>
                            </span>
                        </span>
                    )}
                </div>

                <div className="action-buttons">
                    {isPublished && (
                        <button
                            onClick={handleExternalLinkClick}
                            className="external-link-button"
                            title="Open registration page"
                        >
                            <i className='las la-external-link-alt'></i>
                        </button>
                    )}

                    <UncontrolledDropdown>
                        <DropdownToggle
                            onClick={handleDropdownClick}
                            className="dropdown-toggle-button"
                        >
                            <i className='las la-ellipsis-v'></i>
                        </DropdownToggle>
                        <DropdownMenu right className="dropdown-menu">
                            <DropdownItem onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                toggleDeleteFlowModal(flow);
                            }} className="dropdown-item delete">
                                <i className='las la-trash'></i>
                                <span>Delete</span>
                            </DropdownItem>
                        </DropdownMenu>
                    </UncontrolledDropdown>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="stats-grid">
                {/* Registration Group Stats */}
                {flow.registrationGroup && (
                    <div className="stat-card registration-group">
                        <div className="stat-header">
                            <div className="stat-title">
                                <i className="las la-users"></i>
                                {flow.registrationGroup.name || flow.registrationGroup.label}
                            </div>
                            {flow.registrationGroup.maxCapacityEnabled ? (
                                <div className={`capacity-counter ${capacityPercentage >= 90 ? 'high' : ''}`}>
                                    {totalRegistrations} / {maxCapacity}
                                </div>
                            ) : (<div className={`capacity-counter `}>
                                Unlimited
                            </div>)}
                        </div>

                        {flow.registrationGroup.maxCapacityEnabled && (
                            <div className="progress-container">
                                <div
                                    className={`progress-bar`}
                                    style={{ width: `${capacityPercentage}%` }}
                                ></div>
                            </div>
                        )}
                    </div>
                )}

                {/* Ticket Stats */}
                {flow.ticketingEnabled && (
                    <div className="stat-card tickets">
                        <div className="stat-header">
                            <div className="stat-title">
                                <i className="las la-ticket-alt"></i>
                                Tickets ({flow.tickets.length})
                                {flow.tickets.length > 0 && <>
                                    <i className='las la-info-circle ticketInformationIcon'
                                        id={`ticket-info-${flow._id}`}
                                    />
                                    <UncontrolledTooltip
                                        className="tooltip"
                                        placement="right"
                                        target={`ticket-info-${flow._id}`}
                                    >
                                        {flow.tickets.map(ticket => {
                                            return (
                                                <div key={ticket._id} className="flex jcsb gap20">
                                                    <span className="ticket-name">{ticket.label} {`(${ticket.sold}/${ticket.quantity})`}</span>
                                                    <span className="ticket-price">
                                                        <span>
                                                            ${ticket.price.toLocaleString()}
                                                        </span>
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </UncontrolledTooltip>
                                </>}
                            </div>
                            <div className="ticket-counter">
                                {totalSold} / {totalTickets}
                            </div>
                        </div>

                        <div className="progress-container">
                            <div
                                className="progress-bar"
                                style={{ width: `${ticketSalesPercentage}%` }}
                            ></div>
                        </div>
                    </div>
                )}

                {/* Form Submissions */}
                {/* {flow.form && (
                    <div className="stat-card form-submissions">
                        <div className="stat-title">
                            <i className="las la-clipboard-list"></i>
                            Form Submissions
                        </div>
                        <div className="stat-value">
                            {flow.form.submissions?.length || 0}
                            <span className="stat-label">submissions</span>
                        </div>
                    </div>
                )} */}

                {/* Revenue Stats (replaces Registration Status when tickets exist) */}
                {flow.tickets.length > 0 ? (
                    <div className="stat-card revenue">
                        <div className="stat-title">
                            <i className="las la-dollar-sign"></i>
                            Revenue
                        </div>
                        <div className="stat-value">
                            ${totalRevenue.toLocaleString()}
                            <span className="stat-label">of ${maxRevenue.toLocaleString()}</span>
                        </div>
                    </div>
                ) : (
                    // Only show registration status when no tickets
                    <div className="stat-card registration-status">
                        <div className="stat-title">
                            <i className="las la-chart-line"></i>
                            Registration Status
                        </div>
                        <div className="status-text">
                            {isPublished ? (
                                isActive ? (
                                    <span className="active">Currently accepting registrations</span>
                                ) : isUpcoming ? (
                                    <span className="upcoming">Opens {moment(startDateTimeString).fromNow()}</span>
                                ) : (
                                    <span className="past">Registration closed</span>
                                )
                            ) : (
                                <span className="unpublished">Not published</span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Link>
    );
};

export default RegistrationFlowListItem;